/**
 * @file Admin-owned Ordering list Settings participant.
 */

import {
    appData,
    tempConfigBaselineData,
    tempConfigData,
} from "../../app/state/state.js";
import { applyBrandingVariables } from "../../shared/ui/brandingTokens.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import { changeOrderingListLifecycle } from "../ordering/orderingApi.js";
import {
    bindNativeFieldBehavior,
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
    validateNativeFormFields,
} from "../../shared/forms/nativeFieldStyles.js";

let showInactive = false;
let _renderGeneration = 0;
let updateSharedSaveButton = () => {};
let isCurrentSettingsOwner = () => true;

const EMPTY_USED_LIST_CHOICE_REQUIRED =
    "empty_used_list_choice_required";

function getDraftLists() {
    return Array.isArray(tempConfigData?.orderingLists) ?
        tempConfigData.orderingLists : [];
}

function getBaselineLists() {
    return Array.isArray(tempConfigBaselineData?.orderingLists) ?
        tempConfigBaselineData.orderingLists : [];
}

function ensureDraftLists() {
    if (!tempConfigData) {return null;}
    if (!Array.isArray(tempConfigData.orderingLists)) {
        tempConfigData.orderingLists = [];
    }
    return tempConfigData.orderingLists;
}

function getOrderedEntries(lists = getDraftLists()) {
    return lists.map((list, index) => ({
        list,
        index,
        order: Number.isFinite(Number(list?.order)) ? Number(list.order) : index,
    })).sort((left, right) => {
        const orderDifference = left.order - right.order;
        if (orderDifference !== 0) {return orderDifference;}
        return String(left.list?.id || "").localeCompare(String(right.list?.id || ""));
    });
}

function markDraftDirty() {
    updateSharedSaveButton(true);
}

function getListById(listId) {
    return getDraftLists().find((list) => list?.id === listId) || null;
}

function ownsCurrentSettingsRender(container, generation) {
    return generation === _renderGeneration &&
        container?.isConnected &&
        isCurrentSettingsOwner() &&
        Boolean(container.querySelector("[data-ordering-settings]"));
}

function getComparableListName(value) {
    return String(value || "").trim().normalize("NFKC")
        .replace(/\s+/gu, " ").toLocaleLowerCase("en-US");
}

function getDuplicateListGroupsRequiringConfirmation() {
    const baselineNamesById = new Map(getBaselineLists().map((list) => [
        list?.id,
        getComparableListName(list?.name),
    ]));
    const changedNameIds = new Set(getDraftLists().filter((list) =>
        !baselineNamesById.has(list?.id) ||
        baselineNamesById.get(list?.id) !== getComparableListName(list?.name),
    ).map((list) => list.id));
    const byName = new Map();
    getDraftLists().forEach((list) => {
        const normalizedName = getComparableListName(list?.name);
        if (!normalizedName) {return;}
        const matches = byName.get(normalizedName) || [];
        matches.push(list);
        byName.set(normalizedName, matches);
    });
    return [...byName.values()].filter((matches) =>
        matches.length > 1 &&
        matches.some((list) => changedNameIds.has(list.id)),
    );
}

function confirmDuplicateListName(lists) {
    const matchingList = lists.find((list) => list?.isActive === true) || lists[0];
    const state = matchingList?.isActive === true ? "active" : "inactive";
    const name = escapeHTML(String(matchingList?.name || "this list").trim());
    return new Promise((resolve) => {
        openConfirmationModal(
            "Duplicate Ordering list",
            `An ${state} list called <strong>${name}</strong> already exists. Would you like to proceed?`,
            () => resolve(true),
            false,
            "Save anyway",
            () => resolve(false),
        );
    });
}

function renderToggle() {
    return `
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
            <input type="checkbox" class="${NATIVE_CHECKBOX_CLASSES}" data-ordering-show-inactive ${showInactive ? "checked" : ""}>
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
            </span>
            <span class="text-sm text-gray-600">Show inactive</span>
        </label>
    `;
}

function renderMoveButton(list, direction, disabled) {
    const label = direction === "up" ? "Move list up" : "Move list down";
    return `<button type="button" data-ordering-move="${direction}" data-ordering-list-id="${escapeHTML(list.id)}" class="standard-action-icon-btn ordering-settings-move !p-0 disabled:cursor-not-allowed disabled:opacity-25" aria-label="${label}" ${disabled ? "disabled" : ""}><span class="material-symbols-outlined">${direction === "up" ? "expand_less" : "expand_more"}</span></button>`;
}

function renderListAction(list) {
    const isActive = list?.isActive === true;
    if (list?.hasBeenUsed !== true) {
        return `<button type="button" data-ordering-list-delete class="settings-config-delete" aria-label="Delete unused list"><span class="material-symbols-outlined text-lg">delete</span></button>`;
    }
    return `<button type="button" data-ordering-list-toggle class="settings-config-archive ${isActive ? "" : "settings-config-restore"}" aria-label="${isActive ? "Archive list" : "Unarchive list"}"><span class="material-symbols-outlined text-lg">${isActive ? "archive" : "unarchive"}</span></button>`;
}

function renderListRows() {
    const visible = getOrderedEntries().filter(({list}) =>
        showInactive || list?.isActive === true);
    if (visible.length === 0) {
        return `<p class="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">${showInactive ? "No Ordering lists have been configured." : "No active Ordering lists. Add a list or show inactive lists."}</p>`;
    }
    return visible.map(({list}, index) => {
        const isActive = list.isActive === true;
        return `
            <div class="ordering-settings-row ${isActive ? "" : "opacity-70"}" data-ordering-list-id="${escapeHTML(list.id)}">
                <div class="settings-config-order-controls ordering-settings-order" aria-label="List order controls">
                    ${renderMoveButton(list, "up", index === 0 || !isActive)}
                    ${renderMoveButton(list, "down", index === visible.length - 1 || !isActive)}
                </div>
                <input data-ordering-list-name maxlength="80" required value="${escapeHTML(list.name || "")}" ${isActive ? "" : "disabled"} aria-label="List name" class="settings-config-name ordering-settings-name">
                <div class="ordering-settings-action">
                    ${renderListAction(list)}
                </div>
            </div>
        `;
    }).join("");
}

function renderContent(container, generation) {
    applyBrandingVariables(container, appData);
    if (!tempConfigData) {
        container.innerHTML = `<div class="max-w-4xl rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">Ordering Settings could not be loaded.</div>`;
        return;
    }
    container.innerHTML = `
        <section class="w-full max-w-4xl" data-ordering-settings>
            <div class="rounded-lg bg-white p-5 shadow-md sm:p-6">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h3 class="text-lg font-semibold text-gray-900">Ordering lists</h3>
                    </div>
                    ${renderToggle()}
                </div>
                <div class="mt-5 max-w-3xl">
                    <div class="ordering-settings-header mb-2 text-sm font-medium text-gray-600">
                        <span aria-hidden="true"></span>
                        <span>List name</span>
                        <span aria-hidden="true"></span>
                    </div>
                    <div class="ordering-settings-list" data-ordering-list-rows>${renderListRows()}</div>
                </div>
                <button type="button" data-ordering-add class="settings-add-row-btn mt-3">+ Add List</button>
            </div>
        </section>
    `;
    bindNativeFieldBehavior(container);
    bindInteractions(container, generation);
}

function rerender(container, focusSelector = "") {
    const generation = ++_renderGeneration;
    renderContent(container, generation);
    if (!focusSelector) {return;}
    requestAnimationFrame(() => {
        if (!ownsCurrentSettingsRender(container, generation)) {return;}
        container.querySelector(focusSelector)?.focus({preventScroll: true});
    });
}

function moveList(container, listId, direction) {
    const entries = getOrderedEntries().filter(({list}) => list?.isActive === true);
    const currentIndex = entries.findIndex(({list}) => list.id === listId);
    const nextIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= entries.length) {return;}
    const current = entries[currentIndex].list;
    const next = entries[nextIndex].list;
    const currentOrder = entries[currentIndex].order;
    current.order = entries[nextIndex].order;
    next.order = currentOrder;
    markDraftDirty();
    rerender(container, `[data-ordering-list-id="${CSS.escape(listId)}"] [data-ordering-move="${direction}"]`);
}

function stageListArchive(container, listId, generation) {
    if (!ownsCurrentSettingsRender(container, generation)) {return;}
    const list = getListById(listId);
    if (!list || list.isActive !== true) {return;}
    list.isActive = false;
    markDraftDirty();
    rerender(container, "[data-ordering-add]");
}

function stageListUnarchive(container, listId, generation) {
    if (!ownsCurrentSettingsRender(container, generation)) {return;}
    const list = getListById(listId);
    if (!list || list.isActive === true) {return;}
    list.isActive = true;
    markDraftDirty();
    rerender(container, "[data-ordering-add]");
}

function stageListDelete(container, listId, generation) {
    if (!ownsCurrentSettingsRender(container, generation)) {return;}
    const list = getListById(listId);
    const index = getDraftLists().indexOf(list);
    if (index < 0) {return;}
    getDraftLists().splice(index, 1);
    markDraftDirty();
    rerender(container, "[data-ordering-add]");
}

function isPersistedList(listId) {
    return getBaselineLists().some((list) => list?.id === listId);
}

function areListValuesEqual(left, right) {
    return JSON.stringify(left) === JSON.stringify(right);
}

function reconcileOrderingListLifecycle(authoritativeLists, action, listId) {
    if (!tempConfigData || !tempConfigBaselineData ||
        !Array.isArray(authoritativeLists)) {return;}
    const previousBaseline = getBaselineLists();
    const currentDraft = getDraftLists();
    const baselineById = new Map(previousBaseline.map((list) => [list.id, list]));
    const draftIds = new Set(currentDraft.map((list) => list.id));
    const locallyDeletedIds = new Set(previousBaseline
        .filter((list) => !draftIds.has(list.id))
        .map((list) => list.id));
    const nextDraft = structuredClone(authoritativeLists)
        .filter((list) => !locallyDeletedIds.has(list.id));
    const nextDraftById = new Map(nextDraft.map((list, index) => [
        list.id,
        {index, list},
    ]));

    currentDraft.forEach((draftList) => {
        if (action === "delete" && draftList.id === listId) {return;}
        const baselineList = baselineById.get(draftList.id);
        if (!baselineList) {
            nextDraft.push(structuredClone(draftList));
            return;
        }
        const authoritativeEntry = nextDraftById.get(draftList.id);
        if (!authoritativeEntry) {return;}
        const mergedList = {...authoritativeEntry.list};
        const keys = new Set([
            ...Object.keys(baselineList),
            ...Object.keys(draftList),
        ]);
        keys.forEach((key) => {
            if (areListValuesEqual(draftList[key], baselineList[key])) {return;}
            if (Object.hasOwn(draftList, key)) {
                mergedList[key] = structuredClone(draftList[key]);
            } else {
                delete mergedList[key];
            }
        });
        if (draftList.id === listId && action !== "delete") {
            mergedList.isActive = authoritativeEntry.list.isActive === true;
        }
        nextDraft[authoritativeEntry.index] = mergedList;
    });

    tempConfigData.orderingLists = nextDraft;
    tempConfigBaselineData.orderingLists = structuredClone(authoritativeLists);
    if (appData.config) {
        appData.config.orderingLists = structuredClone(authoritativeLists);
    }
}

function showLifecycleFailure(action, error) {
    const reason = error?.details?.reason || error?.data?.reason || "";
    let title = "Ordering list";
    let message = "The list could not be updated. Please try again or check your connection.";
    if (reason === "list_has_active_items") {
        title = "Cannot Archive Ordering list";
        message = "Close or move every active item before archiving this list.";
    } else if (reason === "list_has_items") {
        title = "Cannot Delete Ordering list";
        message = "Only an empty list can be deleted.";
    } else if (reason === "list_not_used") {
        title = "Cannot Archive Ordering list";
        message = "Unused Ordering lists can be deleted instead of archived.";
    } else if (reason === "list_not_found") {
        message = "This Ordering list no longer exists. Reopen Settings to refresh the list.";
    } else if (reason === "ordering_admin_required") {
        message = "Administrator access is required to change Ordering lists.";
    }
    openConfirmationModal(
        title,
        message,
        () => {},
        false,
        "OK",
        null,
        "",
        "Cancel",
        {hideCancel: true},
    );
}

function showEmptyUsedListIntercept(container, generation, listId) {
    if (!ownsCurrentSettingsRender(container, generation)) {return;}
    const list = getListById(listId);
    if (!list) {return;}
    const resolveReturnFocus = () => container.querySelector(
        `[data-ordering-list-id="${CSS.escape(listId)}"] ` +
        "[data-ordering-list-toggle]",
    ) || container.querySelector("[data-ordering-add]");
    openConfirmationModal(
        "Empty Ordering list",
        `<strong>${escapeHTML(list.name || "This list")}</strong> has no items. Delete it permanently or archive it for later use?`,
        () => applyPersistedListLifecycle({
            action: "archive",
            confirmEmptyArchive: true,
            container,
            generation,
            listId,
        }),
        false,
        "Archive",
        null,
        "Archiving...",
        "Cancel",
        {
            closeOnError: false,
            leftAction: {
                buttonText: "Delete list",
                loadingText: "Deleting...",
                onClick: () => applyPersistedListLifecycle({
                    action: "delete",
                    container,
                    generation,
                    listId,
                }),
                placement: "beforeConfirm",
                variant: "dangerSecondary",
            },
            returnFocusTarget: resolveReturnFocus,
        },
    );
}

async function applyPersistedListLifecycle({
    action,
    confirmEmptyArchive = false,
    container,
    generation,
    listId,
}) {
    try {
        const response = await changeOrderingListLifecycle({
            action,
            ...(confirmEmptyArchive ? {confirmEmptyArchive: true} : {}),
            listId,
        });
        reconcileOrderingListLifecycle(response.orderingLists, action, listId);
        if (!ownsCurrentSettingsRender(container, generation)) {return;}
        if (response.outcome === EMPTY_USED_LIST_CHOICE_REQUIRED) {
            showEmptyUsedListIntercept(container, generation, listId);
            return response;
        }
        rerender(container, "[data-ordering-add]");
        return response;
    } catch (error) {
        setTimeout(() => {
            if (ownsCurrentSettingsRender(container, generation)) {
                showLifecycleFailure(action, error);
            }
        }, 0);
        throw error;
    }
}

function confirmListLifecycle(container, listId, generation, action) {
    const list = getListById(listId);
    if (!list || !ownsCurrentSettingsRender(container, generation)) {return;}
    const persisted = isPersistedList(listId);
    const actions = {
        archive: {
            title: "Archive Ordering list",
            message: `Archive <strong>${escapeHTML(list.name || "this list")}</strong>?`,
            button: "Archive",
            loading: "Archiving...",
            danger: false,
            local: () => stageListArchive(container, listId, generation),
        },
        restore: {
            title: "Unarchive Ordering list",
            message: `Restore <strong>${escapeHTML(list.name || "this list")}</strong>?`,
            button: "Unarchive",
            loading: "Restoring...",
            danger: false,
            local: () => stageListUnarchive(container, listId, generation),
        },
        delete: {
            title: "Delete empty Ordering list",
            message: `Permanently delete <strong>${escapeHTML(list.name || "this list")}</strong>?`,
            button: "Delete list",
            loading: "Deleting...",
            danger: true,
            local: () => stageListDelete(container, listId, generation),
        },
    };
    const selected = actions[action];
    if (!selected) {return;}
    openConfirmationModal(
        selected.title,
        selected.message,
        persisted ?
            () => applyPersistedListLifecycle({
                action,
                container,
                generation,
                listId,
            }) : selected.local,
        selected.danger,
        selected.button,
        null,
        selected.loading,
    );
}

function bindInteractions(container, generation) {
    container.querySelector("[data-ordering-show-inactive]")?.addEventListener(
        "change",
        (event) => {
            showInactive = event.currentTarget.checked;
            rerender(container, "[data-ordering-show-inactive]");
        },
    );
    container.querySelector("[data-ordering-add]")?.addEventListener(
        "click",
        () => {
            const lists = ensureDraftLists();
            if (!lists) {return;}
            const nextOrder = Math.max(-1, ...lists.map((list, index) =>
                Number.isFinite(Number(list?.order)) ? Number(list.order) : index,
            )) + 1;
            const list = {
                id: crypto.randomUUID(),
                name: "",
                order: nextOrder,
                isActive: true,
                hasBeenUsed: false,
            };
            lists.push(list);
            markDraftDirty();
            rerender(container, `[data-ordering-list-id="${CSS.escape(list.id)}"] [data-ordering-list-name]`);
        },
    );
    container.querySelectorAll("[data-ordering-list-name]").forEach((input) => {
        input.addEventListener("input", () => {
            const listId = input.closest("[data-ordering-list-id]")?.dataset.orderingListId;
            const list = getListById(listId);
            if (!list) {return;}
            list.name = input.value;
            markDraftDirty();
        });
    });
    container.querySelectorAll("[data-ordering-move]").forEach((button) => {
        button.addEventListener("click", () => moveList(
            container,
            button.dataset.orderingListId,
            button.dataset.orderingMove,
        ));
    });
    container.querySelectorAll("[data-ordering-list-toggle]").forEach((button) => {
        button.addEventListener("click", () => {
            if (!validateNativeFormFields(container)) {return;}
            const listId = button.closest("[data-ordering-list-id]")?.dataset.orderingListId;
            const list = getListById(listId);
            if (!list) {return;}
            confirmListLifecycle(
                container,
                listId,
                generation,
                list.isActive === true ? "archive" : "restore",
            );
        });
    });
    container.querySelectorAll("[data-ordering-list-delete]").forEach((button) => {
        button.addEventListener("click", () => {
            const listId = button.closest("[data-ordering-list-id]")?.dataset.orderingListId;
            const list = getListById(listId);
            if (!list) {return;}
            confirmListLifecycle(container, listId, generation, "delete");
        });
    });
}

/**
 * Clears Ordering Settings display-only state when leaving Settings.
 * @returns {void}
 */
export function discardOrderingSettingsDraft() {
    showInactive = false;
    _renderGeneration += 1;
}

/**
 * Validates required Ordering list names and confirms duplicate display names
 * before the shared Settings save runs.
 *
 * @returns {Promise<boolean>} Whether the Settings save may continue.
 */
export async function validateOrderingSettingsDraft() {
    const invalidList = getDraftLists().find((list) =>
        !String(list?.name || "").trim(),
    );
    const container = document.querySelector("[data-ordering-settings]");
    if (container && !validateNativeFormFields(container)) {return false;}

    if (invalidList) {
        openConfirmationModal(
            "Ordering lists",
            "Every Ordering list needs a name.",
            () => {},
            false,
            "OK",
            null,
            "",
            "Cancel",
            {hideCancel: true},
        );
        return false;
    }

    for (const matchingLists of getDuplicateListGroupsRequiringConfirmation()) {
        const confirmed = await confirmDuplicateListName(matchingLists);
        if (!confirmed) {return false;}
    }
    return true;
}

/**
 * Renders the config-backed Ordering Settings participant.
 * @param {HTMLElement} container Settings tab container.
 * @param {{ isCurrent?: Function, updateSaveButton?: Function }} [options={}] Render dependencies.
 * @returns {void}
 */
export function renderOrderingSettings(container, options = {}) {
    isCurrentSettingsOwner = options.isCurrent || (() => true);
    updateSharedSaveButton = options.updateSaveButton || (() => {});
    const generation = ++_renderGeneration;
    renderContent(container, generation);
}

/**
 * Invalidates the current Settings render token.
 * @returns {void}
 */
export function invalidateOrderingSettingsRender() {
    _renderGeneration += 1;
}
